import {
  assertValidCampaign,
  deriveCompletionPercentage,
  deriveFrontRunner,
  derivePaidTotal,
  deriveRemaining,
  fixtureCampaign,
} from '../domain'

export interface CampaignSponsorView {
  name: string
  paidAmount: number
  description: string
  installedMonth: string
  proofVerified: boolean
  website?: string
}

export interface CampaignZoneView {
  id: string
  number: number
  status: 'available' | 'reserved' | 'installed'
  roadPixels: number
  price: number
  location: string
  sponsor?: CampaignSponsorView
}

export interface CampaignStoryView {
  id: string
  number: number
  title: string
  body: string
}

export interface CampaignSummaryView {
  goal: number
  paid: number
  remaining: number
  completionPercent: number
  frontRunner: {
    name: string
    paidAmount: number
  }
}

assertValidCampaign(fixtureCampaign)

export const campaignZones: CampaignZoneView[] = fixtureCampaign.zones.map(
  (zone) => {
    const sponsorId = 'sponsorId' in zone ? zone.sponsorId : undefined
    const sponsor = fixtureCampaign.sponsors.find(
      (candidate) => candidate.id === sponsorId,
    )

    return {
      id: zone.id,
      number: zone.number,
      status: zone.status,
      roadPixels: zone.roadPixelCount,
      price: zone.priceUsd,
      location: zone.vehicleLocation,
      sponsor: sponsor
        ? {
            name: sponsor.name,
            paidAmount: sponsor.paidAmountUsd,
            description: sponsor.approvedDescription,
            installedMonth: sponsor.installedMonthYear,
            proofVerified: sponsor.proofVerified,
            website:
              'destinationUrl' in sponsor ? sponsor.destinationUrl : undefined,
          }
        : undefined,
    }
  },
)

export const storyEpisodes: CampaignStoryView[] =
  fixtureCampaign.storyEpisodes.map((episode) => ({
    id: episode.id,
    number: episode.episodeNumber,
    title: episode.title,
    body: episode.body,
  }))

const frontRunner = deriveFrontRunner(
  fixtureCampaign.sponsors,
  fixtureCampaign.ledger,
)

if (!frontRunner) {
  throw new Error('The campaign fixture requires a front runner.')
}

export const campaignSummary: CampaignSummaryView = {
  goal: fixtureCampaign.ledger.goalUsd,
  paid: derivePaidTotal(fixtureCampaign.ledger),
  remaining: deriveRemaining(fixtureCampaign.ledger),
  completionPercent: deriveCompletionPercentage(fixtureCampaign.ledger),
  frontRunner: {
    name: frontRunner.name,
    paidAmount: frontRunner.paidAmountUsd,
  },
}
